/**
 * Everything the Pick'em Game tab reads and does (ISSUE-1304, ISSUE-1309):
 * the pool's rounds from `usePickemPoolGet`, the selected round from
 * `usePickemRoundGet` (the round sits in `?roundId=`, so a refresh or a
 * shared link lands on it), the staged picks, the spread consent gate, the
 * leave guard and the one save through `usePickemPicksSubmit`.
 */
import {
  usePickemPicksSubmit,
  usePickemPoolGet,
  usePickemRoundGet,
} from "@sportsmet/contest-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  PICK_DISCARD_BODY,
  PICK_DISCARD_CANCEL_LABEL,
  PICK_DISCARD_CONFIRM_LABEL,
  PICK_DISCARD_TITLE,
} from "../pick/pickCopy";
import { type typeTeamName, useTeamNames } from "../pick/useTeamNames";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { useSurvivorLeaveGuard } from "../survivorGame/useSurvivorLeaveGuard";
import {
  buildConfidenceRows,
  buildSubmitBody,
  isStageDirty,
  movePickRank,
  parseTiebreakerText,
  selectTeam,
  stagedPickCount,
  stageFromRound,
  type typeConfidenceRow,
  type typePickemStage,
} from "./pickemGameModel";
import type {
  typePickemGame,
  typePickemPoolData,
  typePickemRoundData,
} from "./pickemTypes";

const CLOCK_TICK_MS = 30_000;

export type typePickemGameView =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "empty"; reason: "noContest" | "noRoundsYet" | "contestEnded" }
  | { kind: "ready" };

export type typePendingConsent = {
  eventId: string;
  teamId: string;
  teamName: string;
};

export type typePickemGameTabModel = {
  view: typePickemGameView;
  pool: typePickemPoolData | null;
  round: typePickemRoundData | null;
  isRoundLoading: boolean;
  isRoundFailed: boolean;
  selectedRoundId: string;
  stage: typePickemStage | null;
  teamsById: ReadonlyMap<string, typeTeamName>;
  confidenceRows: typeConfidenceRow[];
  stagedCount: number;
  isDirty: boolean;
  isSaving: boolean;
  isSaved: boolean;
  isTiebreakerInvalid: boolean;
  isFullNoteShown: boolean;
  pendingConsent: typePendingConsent | null;
  nowMs: number;
  timeZone: string;
  selectRound: (roundId: string) => Promise<void>;
  tapTeam: (input: { game: typePickemGame; teamId: string }) => void;
  confirmConsent: () => void;
  cancelConsent: () => void;
  moveRank: (input: { eventId: string; direction: "up" | "down" }) => void;
  setTiebreakerText: (text: string) => void;
  save: () => Promise<void>;
  retry: () => void;
};

export function usePickemGameTab(input: {
  poolId: string;
}): typePickemGameTabModel {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const requestedRoundId =
    typeof search.roundId === "string" ? search.roundId : null;

  const poolQuery = usePickemPoolGet({ poolId: input.poolId });
  const pool = poolQuery.data?.data ?? null;

  const rounds = pool?.rounds ?? [];
  const selectedRoundId =
    requestedRoundId !== null &&
    rounds.some((round) => round.roundId === requestedRoundId)
      ? requestedRoundId
      : (pool?.currentRoundId ?? rounds.at(-1)?.roundId ?? "");

  const roundQuery = usePickemRoundGet(
    { poolId: input.poolId, roundId: selectedRoundId },
    { enabled: selectedRoundId !== "" },
  );
  const round = roundQuery.data?.data ?? null;
  const { teamsById } = useTeamNames({ poolId: input.poolId });
  const submit = usePickemPicksSubmit();

  // The fan's edits since the last save. null means "what the server has".
  const [edits, setEdits] = useState<typePickemStage | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isFullNoteShown, setIsFullNoteShown] = useState(false);
  const [pendingConsent, setPendingConsent] =
    useState<typePendingConsent | null>(null);
  // After a save, the edits stay on screen until the refetch they caused
  // lands, so the cards never flash back to the old picks.
  const savedAtRef = useRef<number | null>(null);
  const [isAwaitingRefresh, setIsAwaitingRefresh] = useState(false);

  const serverStage = useMemo(
    () => (round === null ? null : stageFromRound(round)),
    [round],
  );
  const stage =
    edits !== null && round !== null && edits.roundId === round.round.roundId
      ? edits
      : serverStage;

  useEffect(() => {
    const savedAt = savedAtRef.current;
    if (savedAt !== null && roundQuery.dataUpdatedAt > savedAt) {
      savedAtRef.current = null;
      setEdits(null);
      setIsAwaitingRefresh(false);
      return;
    }
    // The refetch failed: the edits stay on screen and Save comes back.
    if (savedAt !== null && roundQuery.errorUpdatedAt > savedAt) {
      savedAtRef.current = null;
      setIsAwaitingRefresh(false);
    }
  }, [roundQuery.dataUpdatedAt, roundQuery.errorUpdatedAt]);

  const isDirty =
    round !== null && stage !== null ? isStageDirty({ round, stage }) : false;

  useSurvivorLeaveGuard({
    isDirty,
    onDiscard: () => {
      setEdits(null);
    },
  });

  // A clock for the lock lines, and a refetch the moment any game in view
  // passes its lock time, so its card turns locked from the server's answer.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, CLOCK_TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, []);
  const refetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (round === null) return;
    const passed = round.games.find(
      (game) => !game.isLocked && nowMs >= Date.parse(game.locksAt),
    );
    if (passed === undefined) return;
    const key = `${round.round.roundId}:${passed.eventId}`;
    if (refetchedForRef.current === key) return;
    refetchedForRef.current = key;
    void roundQuery.refetch();
  }, [round, nowMs, roundQuery]);

  const timeZone = useMemo(() => resolveViewerTimeZone(), []);

  function edit(next: typePickemStage): void {
    // A tap after a save keeps its edits even if the refetch lands later.
    savedAtRef.current = null;
    setIsAwaitingRefresh(false);
    setIsSaved(false);
    setEdits(next);
  }

  function applyTeam(eventId: string, teamId: string, consent: boolean): void {
    if (round === null || stage === null) return;
    const result = selectTeam({
      round,
      stage,
      eventId,
      teamId,
      acceptsPendingSpread: consent,
    });
    if (!result.ok) {
      setIsFullNoteShown(true);
      return;
    }
    setIsFullNoteShown(false);
    edit(result.stage);
  }

  function tapTeam(tap: { game: typePickemGame; teamId: string }): void {
    if (round === null || stage === null) return;
    const existing = stage.picks[tap.game.eventId];
    const isClearing = existing?.teamId === tap.teamId;
    const needsConsent =
      !isClearing &&
      round.rules.pickBasis === "spread" &&
      tap.game.spread?.isPending === true;
    if (needsConsent) {
      const team = teamsById.get(tap.teamId);
      setPendingConsent({
        eventId: tap.game.eventId,
        teamId: tap.teamId,
        teamName: team?.name ?? "",
      });
      return;
    }
    applyTeam(tap.game.eventId, tap.teamId, false);
  }

  async function selectRound(roundId: string): Promise<void> {
    if (roundId === selectedRoundId) return;
    if (isDirty) {
      const isConfirmed = await confirmDestructive({
        title: PICK_DISCARD_TITLE,
        body: PICK_DISCARD_BODY,
        confirmLabel: PICK_DISCARD_CONFIRM_LABEL,
        cancelLabel: PICK_DISCARD_CANCEL_LABEL,
      });
      if (!isConfirmed) return;
    }
    setEdits(null);
    setIsSaved(false);
    setIsFullNoteShown(false);
    void navigate({
      to: ".",
      replace: true,
      search: (previous: unknown) => ({
        ...(previous as Record<string, unknown>),
        roundId,
      }),
    });
  }

  async function save(): Promise<void> {
    if (round === null || stage === null) return;
    const body = buildSubmitBody({ poolId: input.poolId, round, stage });
    if (body === null) return;
    let result: Awaited<ReturnType<typeof submit.mutateAsync>>;
    try {
      result = await submit.mutateAsync(body);
    } catch {
      // The shared mutation cache raised the offline modal already.
      return;
    }
    if (showApiError(result)) return;
    savedAtRef.current = Date.now();
    setIsAwaitingRefresh(true);
    setIsSaved(true);
    setIsFullNoteShown(false);
  }

  let view: typePickemGameView;
  if (poolQuery.isLoading) {
    view = { kind: "loading" };
  } else if (
    poolQuery.isError ||
    pool === null ||
    Boolean(poolQuery.data?.error)
  ) {
    view = { kind: "failed" };
  } else if (pool.emptyState !== "none" && rounds.length === 0) {
    view = { kind: "empty", reason: pool.emptyState };
  } else if (rounds.length === 0) {
    view = { kind: "empty", reason: "noRoundsYet" };
  } else {
    view = { kind: "ready" };
  }

  const tiebreakerText = stage?.tiebreakerText ?? "";

  return {
    view,
    pool,
    round,
    isRoundLoading: roundQuery.isLoading,
    isRoundFailed: roundQuery.isError || Boolean(roundQuery.data?.error),
    selectedRoundId,
    stage,
    teamsById,
    confidenceRows:
      round !== null && stage !== null
        ? buildConfidenceRows({ round, stage })
        : [],
    stagedCount: stage === null ? 0 : stagedPickCount(stage),
    isDirty,
    // Holding Save until the saved picks come back keeps a second tap from
    // sending the same change twice.
    isSaving: submit.isPending || isAwaitingRefresh,
    isSaved: isSaved && !isDirty,
    isTiebreakerInvalid: !parseTiebreakerText(tiebreakerText).ok,
    isFullNoteShown,
    pendingConsent,
    nowMs,
    timeZone,
    selectRound,
    tapTeam,
    confirmConsent: () => {
      if (pendingConsent === null) return;
      applyTeam(pendingConsent.eventId, pendingConsent.teamId, true);
      setPendingConsent(null);
    },
    cancelConsent: () => {
      setPendingConsent(null);
    },
    moveRank: (move) => {
      if (round === null || stage === null) return;
      edit(movePickRank({ round, stage, ...move }));
    },
    setTiebreakerText: (text) => {
      if (stage === null) return;
      edit({ ...stage, tiebreakerText: text });
    },
    save,
    retry: () => {
      void poolQuery.refetch();
      if (selectedRoundId !== "") void roundQuery.refetch();
    },
  };
}
